import React, { createContext, useContext, useState, useCallback } from 'react';
import { generateId } from '../utils/strategyUtils';

/**
 * TrendLineDrawingContext - Manages temporary trendline data during strategy creation
 * 
 * This context holds drawn trendlines that are scoped to the strategy creation session.
 * Data is cleared when the user navigates away from strategy creation or saves the strategy.
 */

const TrendLineDrawingContext = createContext(null);

/**
 * TrendLineDrawingProvider - Provider component for trendline drawing context
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const TrendLineDrawingProvider = ({ children }) => {
  // Drawn trendlines stored during strategy creation session
  const [drawnTrendLines, setDrawnTrendLines] = useState([]);
  
  // Currently selected trendline for a condition
  const [selectedTrendLine, setSelectedTrendLine] = useState(null);
  
  // Callback to be called when a trendline is selected from the chart
  const [onSelectCallback, setOnSelectCallback] = useState(null);

  /**
   * Add a new trendline from the chart
   * @param {Object} trendLine - Trendline data with coordinates
   */
  const addTrendLine = useCallback((trendLine) => {
    const newTrendLine = {
      ...trendLine,
      id: trendLine.id || generateId('trendline'),
      createdAt: new Date().toISOString(),
    };
    setDrawnTrendLines(prev => [...prev, newTrendLine]);
    return newTrendLine;
  }, []);

  /**
   * Update an existing trendline
   * @param {string} id - Trendline ID
   * @param {Object} updates - Updated trendline data
   */
  const updateTrendLine = useCallback((id, updates) => {
    setDrawnTrendLines(prev => 
      prev.map(line => line.id === id ? { ...line, ...updates } : line)
    );
  }, []);

  /**
   * Remove a trendline
   * @param {string} id - Trendline ID to remove
   */
  const removeTrendLine = useCallback((id) => {
    setDrawnTrendLines(prev => prev.filter(line => line.id !== id));
  }, []);

  /**
   * Clear all drawn trendlines (called on strategy save or navigation away)
   */
  const clearAllTrendLines = useCallback(() => {
    setDrawnTrendLines([]);
    setSelectedTrendLine(null);
    setOnSelectCallback(null);
  }, []);

  /**
   * Select a trendline and trigger the callback
   * @param {Object} trendLine - Selected trendline
   */
  const selectTrendLine = useCallback((trendLine) => {
    setSelectedTrendLine(trendLine);
    if (onSelectCallback) {
      onSelectCallback(trendLine);
    }
  }, [onSelectCallback]);

  /**
   * Register a callback to be called when a trendline is selected
   * @param {Function} callback - Callback function (trendLine) => void
   */
  const registerSelectCallback = useCallback((callback) => {
    setOnSelectCallback(() => callback);
  }, []);

  /**
   * Unregister the select callback
   */
  const unregisterSelectCallback = useCallback(() => {
    setOnSelectCallback(null);
  }, []);

  /**
   * Get trendlines filtered by symbol
   * @param {string} symbol - Stock symbol
   * @returns {Array} Filtered trendlines
   */
  const getTrendLinesBySymbol = useCallback((symbol) => {
    if (!symbol) return drawnTrendLines;
    return drawnTrendLines.filter(line => 
      line.symbol?.toUpperCase() === symbol.toUpperCase()
    );
  }, [drawnTrendLines]);

  const value = {
    // State
    drawnTrendLines,
    selectedTrendLine,
    
    // Actions
    addTrendLine,
    updateTrendLine,
    removeTrendLine,
    clearAllTrendLines,
    selectTrendLine,
    registerSelectCallback,
    unregisterSelectCallback,
    getTrendLinesBySymbol,
  };

  return (
    <TrendLineDrawingContext.Provider value={value}>
      {children}
    </TrendLineDrawingContext.Provider>
  );
};

/**
 * useTrendLineDrawing - Hook to access trendline drawing context
 * @returns {Object} Context value
 * @throws {Error} If used outside of TrendLineDrawingProvider
 */
export const useTrendLineDrawing = () => {
  const context = useContext(TrendLineDrawingContext);
  if (!context) {
    throw new Error('useTrendLineDrawing must be used within a TrendLineDrawingProvider');
  }
  return context;
};

export default TrendLineDrawingContext;
